import React from "react";
import { Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Query, queryOps } from "system/query/Query";
import QuerySegmentContainer, { QuerySegmentProps, spanSide } from "components/queries/queryBuilder/common/QuerySegmentContainer";
import { QueryBuilderContainerBox } from "components/queries/queryBuilder/common/QueryBuilderContainerBox";
import { QueryBuilderLabel } from "components/queries/queryBuilder/common/QueryBuilderLabel";
import { colorsAndLabelsByOp, queryOpIsBranch } from "system/query/QueryUtility";
import { QueryBuilderDropTarget } from "components/queries/queryBuilder/common/QueryBuilderDropTarget";
import { QuerySpan } from "system/query/QuerySpan";
import { useDroppable } from "@dnd-kit/core";
import { QueryBuilderInnerLabel } from "../../common/QueryBuilderInnerLabel";
import { QueryBuilderFieldLabel } from "../../common/QueryBuilderFieldLabel";

export interface QuerySpanBuilderProps extends QuerySegmentProps {
  querySpan: QuerySpan;
}

export default function QuerySpanBuilder(props: QuerySpanBuilderProps): JSX.Element {
  const theme = useTheme();
  const {i18n} = useTranslation();

  const { backgroundColor, borderColor, borderColorTop, label } = colorsAndLabelsByOp(i18n, theme.colors.namedColors, queryOps.span);

  const leftDroppable = useDroppable({
    id: props.path + ".left",
  });
  const rightDroppable = useDroppable({
    id: props.path + ".right",
  });


  const showLeftDropTarget = !props.querySpan.left || !queryOpIsBranch(props.querySpan.left.op);
  const showRightDropTarget = !props.querySpan.right || !queryOpIsBranch(props.querySpan.right.op);

  return (
    <QueryBuilderContainerBox
        path={props.path}
        draggable={props.path?.length > 0}
        backgroundColor={backgroundColor}
        borderColor={borderColor}
        borderColorTop={borderColorTop}
        onClick={() => props.editSegment(props.querySpan, props.parent, props.inSpan, props.spanSide)} >
      <QueryBuilderInnerLabel><strong>{label}</strong></QueryBuilderInnerLabel>
      <QueryBuilderFieldLabel
        queryInfo={props.queryInfo}
        isViewerMode={false}
        fieldId={props.querySpan.field}
        borderColor={borderColor}
        borderColorTop={borderColorTop}
        inline={true} />
      <Box ref={leftDroppable.setNodeRef}>
      {props.querySpan.left &&
        <QuerySegmentContainer
        depth={props.depth + 1}
        parent={props.querySpan}
        path={`${props.path}.left`}
        isAddingActive={!!leftDroppable.active}
        inSpan={true}
        spanSide={spanSide.left}
        queryInfo={props.queryInfo}
        querySegment={props.querySpan.left}
        editSegment={props.editSegment}
        addSegment={props.addSegment} />
      }
      {showLeftDropTarget &&
        <QueryBuilderDropTarget
          borderColor={borderColor} isOver={leftDroppable.isOver}
          hasContentsAbove={props.querySpan.left !== undefined}
          onClick={(event:any) => {
            event?.stopPropagation();
            props.addSegment(props.querySpan, true, spanSide.left);
          }} />
      }
      </Box>
      <QueryBuilderInnerLabel>
        {props.querySpan.ordered &&
          <strong>{i18n.t("query-form.within-span-ordered", {distance: props.querySpan.distance})}</strong>
        }
        {!props.querySpan.ordered &&
          <strong>{i18n.t("query-form.within-span", {distance: props.querySpan.distance})}</strong>
        }
      </QueryBuilderInnerLabel>
      <Box ref={rightDroppable.setNodeRef}>
      {props.querySpan.right &&
        <QuerySegmentContainer
          depth={props.depth + 1}
          parent={props.querySpan}
          path={`${props.path}.right`}
          isAddingActive={!!rightDroppable.active}
          inSpan={true}
          spanSide={spanSide.right}
          queryInfo={props.queryInfo}
          querySegment={props.querySpan.right}
          editSegment={props.editSegment}
          addSegment={props.addSegment} />
        }
      {showRightDropTarget &&
        <QueryBuilderDropTarget
          borderColor={borderColor} isOver={rightDroppable.isOver}
          hasContentsAbove={props.querySpan.right !== undefined}
          onClick={(event:any) => {
            event?.stopPropagation();
            props.addSegment(props.querySpan, true, spanSide.right);
          }} />
      }
      </Box>
    </QueryBuilderContainerBox>
  );
}